import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, ShieldAlert } from "lucide-react";

export default function Messages() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLang();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const loadThread = async (other) => {
    try {
      const { data } = await api.get(`/messages/${other}`);
      setMsgs(data);
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
    } catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { if (id) loadThread(id); }, [id]);

  const send = async () => {
    if (!text.trim() || !id) return;
    setBusy(true);
    try {
      const { data } = await api.post("/messages", { to_user_id: id, text });
      setMsgs((prev) => [...prev, data]);
      setText("");
      if (data.original_had_pii) {
        toast.warning("Contact info was auto-masked in your message.");
      }
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
      {/* Message pane (thread list moved to /inbox) */}
      <section className="border border-[#D6D2C9] bg-white flex flex-col h-[calc(100vh-140px)]" style={{ borderRadius: 8 }}>
        {!id ? (
          <div className="flex-1 grid place-items-center text-[#57534E] p-10 text-center">
            Select a conversation from <a href="/inbox?tab=threads" className="text-[#C85A53] underline">Inbox</a>, or open a profile and tap Message.
          </div>
        ) : (
          <>
            <div className="p-3 border-b border-[#D6D2C9] flex items-center gap-2 text-xs text-[#57534E]">
              <ShieldAlert className="w-4 h-4 text-[#C85A53]" />
              Phone numbers, emails and links are auto-masked for safety.
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3">
              {msgs.map((m, i) => {
                const mine = m.from_id === user?.id;
                const stableKey = m.id || m._id || `${m.from_id || "?"}-${m.at || m.created_at || i}`;
                return (
                  <div key={stableKey} data-testid={`msg-${i}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${
                      mine ? "bg-[#C85A53] text-white" : "bg-[#EAE6DF] text-[#1C1917]"
                    }`}>
                      {m.text}
                    </div>
                  </div>
                );
              })}
              {msgs.length === 0 && <div className="text-center text-sm text-[#57534E] py-10">{t("no_msg")}</div>}
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="p-3 border-t border-[#D6D2C9] flex gap-2"
            >
              <Input
                data-testid="msg-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type a message…"
                className="border-[#D6D2C9]"
              />
              <Button
                data-testid="msg-send"
                type="submit"
                disabled={busy}
                className="btn-pill bg-[#C85A53] hover:bg-[#A94842] text-white"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
